import React from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import Button from "@components/Button";
import Input from "@components/Input";
import SizedBox from "@components/SizedBox";
import { ROUTES } from "@stores/AccountStore";
import { observer } from "mobx-react-lite";
import { useStores } from "@stores";
import { useLang } from "@src/hooks/useLang";
import AuthLayout, { ErrorAlert } from "@components/AuthLayout";
import { authClient } from "@services/authService";
import Text from "@components/Text";
import {
  clearPendingOAuthQuery,
  followOAuthRedirect,
  peekPendingOAuthQuery,
} from "@src/utils/oauthRequest";

// Give the first email time to actually land (Gmail can delay a minute,
// especially into the Updates tab) before offering a resend.
const RESEND_COOLDOWN_S = 120;

/**
 * 6-digit email verification code entry. Reached after signup or after a login
 * attempt on an unverified account — both already trigger a code email
 * server-side, so this screen never auto-sends on mount (it would double-send).
 */
const VerifyEmailScreen: React.FC = () => {

  const lang = useLang();
  const { accountStore } = useStores();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const email: string =
    (location.state as { email?: string } | null)?.email ??
    searchParams.get("email") ??
    "";
  const t = (lang as any).auth ?? {};

  const [otp, setOtp] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [info, setInfo] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(RESEND_COOLDOWN_S);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  if (accountStore.authenticated) {
    return <Navigate to={ROUTES.FIELDS} />;
  }
  if (!email) {
    return <Navigate to={ROUTES.LOGIN} />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      // Signing up while connecting an AI assistant: verification signs the
      // user in, and with the saved OAuth request the backend continues to the
      // consent screen ({ redirect, url }) instead of the app.
      const oauthQuery = peekPendingOAuthQuery();
      if (oauthQuery) {
        const { data, error } = await authClient.$fetch<{ redirect?: boolean; url?: string }>(
          "/email-otp/verify-email",
          { method: "POST", body: { email, otp, oauth_query: oauthQuery } }
        );
        if (error) {
          if ((error as { error?: string }).error === "invalid_signature") {
            // The request expired during the detour: verify without it.
            clearPendingOAuthQuery();
            setError(lang.aiConnector.requestExpired);
          } else {
            setError(error.message || "Verification failed");
          }
          setLoading(false);
          return;
        }
        clearPendingOAuthQuery();
        if (followOAuthRedirect(data)) return;
        await accountStore.auth();
        setLoading(false);
        return;
      }
      const { error } = await authClient.emailOtp.verifyEmail({ email, otp });
      if (error) {
        setError(error.message || "Verification failed");
        setLoading(false);
        return;
      }
      // autoSignInAfterVerification set the session cookie — load it; the
      // `authenticated` guard above then navigates into the app.
      await accountStore.auth();
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError(null);
    setInfo(null);
    try {
      const { error } = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: "email-verification",
      });
      if (error) {
        setError(error.message || "Could not send the code");
        return;
      }
      setCooldown(RESEND_COOLDOWN_S);
      setInfo(t.codeSent ?? "A new code has been sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send the code");
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} style={{ width: 336 }}>
        <ErrorAlert enabled={!!error}>{error}</ErrorAlert>
        <Text size={18} weight={500} style={{ textAlign: "center", width: "100%" }}>
          {t.verifyEmailTitle ?? "Verify your email"}
        </Text>
        <SizedBox height={8} />
        <Text size={14} color="#899093" style={{ textAlign: "center", width: "100%" }}>
          {(t.verifyEmailDescription ?? "We sent a 6-digit code to {email}").replace(
            "{email}",
            email
          )}
        </Text>
        <SizedBox height={8} />
        <Text size={13} color="#899093" style={{ textAlign: "center", width: "100%" }}>
          {t.spamHint ?? "Didn't get it right away? Please check your spam folder."}
        </Text>
        <SizedBox height={16} />
        <Input
          label={t.verifyCodeTitle ?? "Verification code"}
          placeholder={t.verifyCodeField ?? "Enter code"}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          style={{ textAlign: "center", letterSpacing: 8, fontSize: 20 }}
          required
        />
        <SizedBox height={24} />
        <Button type="submit" disabled={loading || otp.length !== 6}>
          {t.verifyButton ?? "Verify"}
        </Button>
        <SizedBox height={16} />
        {info && (
          <>
            <Text size={14} color="#68AD18" style={{ textAlign: "center", width: "100%" }}>
              {info}
            </Text>
            <SizedBox height={8} />
          </>
        )}
        <Text
          size={14}
          color={cooldown > 0 ? "#899093" : "#68AD18"}
          style={{
            cursor: cooldown > 0 ? "default" : "pointer",
            textAlign: "center",
            width: "100%",
          }}
          onClick={handleResend}
        >
          {cooldown > 0
            ? (t.resendCodeIn ?? "Resend code in {seconds}s").replace(
                "{seconds}",
                String(cooldown)
              )
            : t.resendCode ?? "Resend code"}
        </Text>
      </form>
    </AuthLayout>
  );
};

export default observer(VerifyEmailScreen);
